import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ActionButton, Flex, Item, TabList, TabPanels } from '@adobe/react-spectrum';
import { DeepLinkTabs } from '../../utils/deepLink/DeepLinkTabs';
import Data from '@spectrum-icons/workflow/Data';
import ViewList from '@spectrum-icons/workflow/ViewList';
import isMobile from 'ismobilejs';
import { SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import './UserDetails.css';
import { getGrades } from '../../data/ScoreDisplayUtils';
import { Helmet } from 'react-helmet';
import { ResultsListing } from '../resultsListing/ResultsListing';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import GearsEdit from '@spectrum-icons/workflow/GearsEdit';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import { getUser } from '../../data/DynamoConnector';
import { resultsColumns } from '../resultsListing/columns/ResultsColumns';
import { resultsColumnRenderers } from '../resultsListing/renderers/ResultsColumnRenderers';
import { resultsCellRenderers } from '../resultsListing/renderers/ResultsCellRenderers';
import {
    fetchUserDetailsItems,
    setUserDetailsSortBy,
    setUserDetailsSortDirection,
    userDetailsFetchFiltered,
} from '../../data/redux/slices/userDetailsSlice';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { AvatarImage } from '../avatarImage/AvatarImage';
import { BreadCrumbShareButton } from '../breadcrumbs/buttons/BreadCrumbShareButton';
import { PrintDialog } from '../widgets/printDialog/PrintDialog';
import { BreadCrumbImportButton } from '../breadcrumbs/buttons/BreadCrumbImportButton';
import { BreadCrumbActionBar } from '../breadcrumbs/BreadCrumbActionBar';
import { setSelectedListIndex } from '../../data/redux/slices/usersSlice';
import { formatNumber, truncateToOneDecimal } from '../common/cardStats/formatters';
import { capBracketRating, capPowerLevel } from '../../data/displayCaps';
import { UserStatsPanel } from './UserStatsPanel';

const PROVIDER_FAVICONS = {
    moxfield: 'https://moxfield.com/favicon.png',
    archidekt: 'https://archidekt.com/favicon.ico',
    tappedout: 'https://tappedout.net/favicon.ico',
};

function getFavicon(source) {
    return PROVIDER_FAVICONS[source] || '';
}

export function UserDetails() {
    const dispatch = useDispatch();
    let listItems = useSelector((state) => state?.userDetails?.listItems);
    listItems = (listItems || []).filter((item) => item && item?.id);
    const userId = useSelector((state) => state?.userDetails?.profile?.id);
    const nextCursor = useSelector((state) => state?.userDetails?.nextCursor);
    const isFetching = useSelector((state) => state?.userDetails?.isFetching);
    const loadingState = useSelector((state) => state?.userDetails?.loadingState);
    const searchFilters = useSelector((state) => state?.userDetails?.filters);
    const sortBy = useSelector((state) => state?.userDetails?.sortBy);
    const sortDirection = useSelector((state) => state.userDetails.sortDirection);
    const selectedListKey = useSelector((state) => state?.userDetails?.selectedListKey);
    const selectedListIndex = useSelector((state) => state?.userDetails?.selectedListIndex);
    const isRefreshing = useSelector((state) => state?.userDetails?.isRefreshing);
    const isImporting = useSelector((state) => state.import.isImporting);
    const saltRating = useSelector((state) => state?.userDetails?.profile?.saltRating);
    const powerLevelRating = useSelector((state) => state?.userDetails?.profile?.powerLevelRating);
    const synergyRating = useSelector((state) => state?.userDetails?.profile?.synergyRating);
    const bracketRating = useSelector((state) => state?.userDetails?.profile?.bracketRating);
    const count = useSelector((state) => state?.userDetails?.profile?.count);

    const username = useSelector((state) => state?.userDetails?.profile?.metadata?.profileName);
    const bio = useSelector((state) => state?.userDetails.profile?.metadata?.bio);
    const linkedProfiles = useSelector((state) => state?.userDetails.profile?.profiles);
    const avatarUrl = useSelector((state) => state?.userDetails?.profile?.metadata?.avatarUri);
    const subscriberTier = useSelector(
        (state) => state?.userDetails?.profile?.preferences?.subscriberFeatures?.subscriberTier
    );
    const hideLinkedAccounts = useSelector(
        (state) => state?.userDetails?.preferences?.userDetails?.profile?.userPreferences?.hideLinkedAccounts
    );
    const showSubscriberLevelOnProfile = useSelector(
        (state) =>
            state?.userDetails.preferences?.userDetails?.profile?.subscriberFeatures?.perks
                ?.showSubscriberLevelOnProfile
    );

    const handleSortChange = (sortIndex) => {
        dispatch(setUserDetailsSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchUserDetailsItems(null, searchFilters, true, SORT_MAP[sortIndex], sortDirection, userId));
    };

    const handleSortDirectionChange = (direction) => {
        dispatch(setUserDetailsSortDirection(direction));
        dispatch(fetchUserDetailsItems(null, searchFilters, true, sortBy, direction, userId));
    };

    const handleFiltersChange = (filters) => {
        dispatch(userDetailsFetchFiltered(filters, sortBy, sortDirection, userId));
    };

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const [mobile, setMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        if (isMobile(window.navigator).phone) return true;
        return window.matchMedia('(max-width: 768px)').matches;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mql = window.matchMedia('(max-width: 768px)');
        const onChange = () => setMobile(isMobile(window.navigator).phone || mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    const handleLoadMore = () => {
        if (!isFetching && nextCursor !== null) {
            dispatch(fetchUserDetailsItems(nextCursor, searchFilters, false, sortBy, sortDirection, userId));
        }
    };

    const helmetName = username ? `Users - ${username}` : `User Details - Loading...`;
    const helmetCanonical = `https://www.commandersalt.com/user`;
    const helmetDescription = `The saltiest MtG deck builders on the planet!`;
    const helmetTitle = `Commandersalt.com - The saltiest MtG deck builders on the planet!`;

    const grades = getGrades({
        saltRating,
        powerLevelRating,
        synergyRating,
        bracketRating,
    });
    const subscriberTierLabel = parseInt(subscriberTier) ? `L${subscriberTier}` : 'MAX';
    const hasLinkedProfiles =
        !hideLinkedAccounts &&
        linkedProfiles &&
        Object.keys(linkedProfiles).some((provider) => Object.keys(linkedProfiles[provider] || {}).length > 0);
    const deckCountLabel = count != null ? `${count} deck${count === 1 ? '' : 's'}` : '';

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs
                content={
                    <BreadCrumbActionBar maxVisibleMobile={2}>
                        {getUser() && getUser()?.profile?.sub === userId && (
                            <ActionButton
                                aria-label="Edit profile"
                                onPress={() => dispatch(setAppRoute({ route: '/settings' }))}
                            >
                                <span className="bcBtnLabel">Edit profile</span>
                                <GearsEdit />
                            </ActionButton>
                        )}
                        {getUser() && getUser()?.profile?.sub === userId && (
                            <PrintDialog authorId={userId} pageType={'user'} name={username} />
                        )}
                        <BreadCrumbShareButton label={'Share profile'} isDisabled={isImporting || isRefreshing} />
                        <BreadCrumbImportButton isDisabled={isImporting || isRefreshing} />
                    </BreadCrumbActionBar>
                }
            />
            {isFetching && <GenericLoadingDialog />}

            <div className="heroPanelWrap">
                <div className="heroPanelCard panelGradient userHeroCard">
                    <div className="heroPanelImages">
                        {avatarUrl ? (
                            <AvatarImage size={200} avatarUrl={avatarUrl} />
                        ) : (
                            <img
                                src="/resources/blank-user-avatar.png"
                                alt={username || 'avatar'}
                                className="heroPanelImage heroPanelImage--avatar"
                            />
                        )}
                        {showSubscriberLevelOnProfile && (
                            <span className="userSupporterBadge">
                                <span className="userSupporterBadgeTier">{subscriberTierLabel}</span>
                                <span className="userSupporterBadgeLabel">Supporter</span>
                            </span>
                        )}
                    </div>
                    <div className="heroPanelInfo">
                        <div className="heroPanelEyebrow">
                            User Profile{deckCountLabel ? ` · ${deckCountLabel}` : ''}
                        </div>
                        <div className="heroPanelName">{username || 'Loading…'}</div>
                        <div className="heroPanelStatsGrid">
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Power Level</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.powerlevel?.color }}>
                                    {formatNumber(capPowerLevel(powerLevelRating), 1)}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Bracket</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.bracket?.color }}>
                                    {truncateToOneDecimal(capBracketRating(bracketRating), '—')}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Saltiness</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.salt?.color }}>
                                    {formatNumber(saltRating, 1)}
                                </div>
                            </div>
                            <div className="heroPanelStat">
                                <div className="heroPanelStatLabel">Synergy</div>
                                <div className="heroPanelStatValue" style={{ color: grades?.synergy?.color }}>
                                    {formatNumber(synergyRating, 1)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {(bio || hasLinkedProfiles) && (
                <div className="userDetailRow">
                    {bio && (
                        <section className="userDetailCard">
                            <span className="userDetailEyebrow">Bio</span>
                            <p className="userDetailBio">{bio}</p>
                        </section>
                    )}
                    {hasLinkedProfiles && (
                        <section className="userDetailCard">
                            <span className="userDetailEyebrow">Linked profiles</span>
                            <div className="userLinkedProfileList">
                                {Object.keys(linkedProfiles).map((provider) =>
                                    Object.keys(linkedProfiles[provider] || {}).map((handle) => {
                                        const url = linkedProfiles[provider][handle]?.profileUrl;
                                        return (
                                            <button
                                                type="button"
                                                key={`link_${provider}_${handle}`}
                                                className="userLinkedProfilePill"
                                                onClick={() => url && window.open(url, '_blank')}
                                                disabled={!url}
                                                title={
                                                    url ? `Open ${handle} on ${provider}` : `${handle} on ${provider}`
                                                }
                                            >
                                                <img
                                                    src={getFavicon(provider)}
                                                    alt=""
                                                    className="userLinkedProfileIcon"
                                                />
                                                <span className="userLinkedProfileMeta">
                                                    <span className="userLinkedProfileProvider">{provider}</span>
                                                    <span className="userLinkedProfileHandle">{handle}</span>
                                                </span>
                                                <LinkOut size="XS" UNSAFE_className="userLinkedProfileChevron" />
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </section>
                    )}
                </div>
            )}

            {(() => {
                const deckListColumn = (
                    <div
                        className={`userDetailsColumn${mobile ? '' : ' panelGradient'}`}
                        style={
                            mobile
                                ? { display: 'initial', width: '100%', minWidth: 0 }
                                : {
                                      display: 'initial',
                                      flex: '1 1 380px',
                                      width: 380,
                                      minWidth: 320,
                                      maxWidth: 500,
                                  }
                        }
                    >
                        <ResultsListing
                            includeSortByCount={false}
                            includeSortByIngestDate={true}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            handleSortChange={handleSortChange}
                            handleSortDirectionChange={handleSortDirectionChange}
                            disableNavChanges={true}
                            allowDeckPreview={false}
                            handleIndexSelection={handleIndexSelection}
                            selectedListIndex={selectedListIndex}
                            selectedListIndexPrefix={'users_'}
                            selectedListKey={selectedListKey}
                            hasMore={nextCursor !== null}
                            columns={resultsColumns.LEADERBOARD_COMPACT()}
                            columnRenderer={resultsColumnRenderers.LEADERBOARD}
                            listItems={listItems}
                            cellRenderer={resultsCellRenderers.LEADERBOARD}
                            handleLoadMore={handleLoadMore}
                            loadingState={loadingState}
                            showFilterPanel={true}
                            filterContext="decks"
                            onFiltersChange={handleFiltersChange}
                            filters={searchFilters}
                            containerWidth="100%"
                            containerMaxWidth="100%"
                            outerWidth="100%"
                            outerMarginTop={0}
                            transparentContainer
                        />
                    </div>
                );

                const statsColumn = (
                    <div
                        className={`userDetailsColumn${mobile ? '' : ' panelGradient'}`}
                        style={
                            mobile
                                ? { display: 'initial', width: '100%', minWidth: 0 }
                                : {
                                      display: 'initial',
                                      flex: '3 1 600px',
                                      width: 600,
                                      minWidth: 320,
                                      minHeight: window.innerHeight - 70,
                                  }
                        }
                    >
                        <UserStatsPanel />
                    </div>
                );

                if (mobile) {
                    return (
                        <div className="userDetailsMobileTabs panelGradient">
                            <DeepLinkTabs defaultSelectedKey="stats" isEmphasized UNSAFE_style={{ padding: 0 }}>
                                <TabList marginStart="10px">
                                    <Item key="stats">
                                        <Data size="XS" />
                                        Statistics
                                    </Item>
                                    <Item key="decks">
                                        <ViewList size="XS" />
                                        Deck Lists
                                    </Item>
                                </TabList>
                                <TabPanels marginTop="10px">
                                    <Item key="stats">{statsColumn}</Item>
                                    <Item key="decks">{deckListColumn}</Item>
                                </TabPanels>
                            </DeepLinkTabs>
                        </div>
                    );
                }

                return (
                    <Flex
                        direction="row"
                        justifyContent="space-between"
                        columnGap={20}
                        rowGap={30}
                        marginTop={20}
                        alignSelf="center"
                        alignItems="start"
                        wrap
                        maxWidth="calc(100vw - 20px)"
                        width={1400}
                    >
                        {deckListColumn}
                        {statsColumn}
                    </Flex>
                );
            })()}
        </Flex>
    );
}
